// Speculative warming for the Bunny Stream HLS the homepage hero plays.
//
// A hard load is already covered: _document.js preconnects to the Bunny origin
// and the hero's poster is preloaded as the LCP image, so the video has the
// whole page load (and, on the homepage, the 5.55s loader) to buffer behind it.
// A client-side navigation gets none of that. The preconnect socket is long
// closed, nothing on the source page has touched the video, and arriving at the
// homepage costs a fresh handshake plus three sequential round trips —
// master playlist -> level playlist -> first segment — before a single frame of
// video exists.
//
// Cache lifetimes decide *when* each piece can usefully be warmed. Measured on
// the pull zone:
//
//   playlist.m3u8            cache-control: public, max-age=30
//   <level>/video.m3u8       cache-control: public, max-age=30
//   <level>/video0.ts        cache-control: public, max-age=2592000
//
// The playlists expire in 30 seconds, so warming them on idle at page load is
// pointless — they'd be stale long before anyone clicks. They have to ride a
// navigation *intent* signal instead (link hover/focus, or routeChangeStart as
// a backstop for keyboard and programmatic navs). The first segment is cached
// for 30 days, so fetching it is a genuine one-off per visitor and is reused for
// the rest of the session.
//
// Everything here is best-effort and must stay silent on failure: a blocked
// fetch, a referrer-restricted pull zone or an offline client should cost
// nothing more than the video loading the way it did before.

import { preloadHls } from './useHlsVideo';

// The Bunny Stream video library host. Shared with _document.js's preconnect
// list — if the library ever changes, this is the one place to change it. It's
// the `vz-*.b-cdn.net` host in the `mediaItemUrl` of any video attachment in
// WordPress.
export const MEDIA_ORIGIN = 'https://vz-3eee71aa-d42.b-cdn.net';

// Bunny's playlists carry max-age=30. Re-warming inside that window is a
// no-op that still costs a request, so remember what we fetched and when.
const PLAYLIST_TTL_MS = 30_000;

// Route path -> media URLs to warm when a navigation to it looks imminent.
// A registry rather than props/context so SmartLink doesn't have to be handed
// global data just to answer "does the page behind this link start with a
// video?", and so more routes can be added without touching the link component.
const routeMedia = new Map();

// A failed warm is remembered for a fixed cooldown regardless of the caller's
// TTL. Without it, a pull zone that rejects this origin's referrer (every local
// dev server, for one) would re-request on every single hover.
const FAILURE_COOLDOWN_MS = 60_000;

// url -> { at, ok } for the last warm attempt, in-flight ones included.
const warmed = new Map();

let preconnectEl = null;

// Menu URLs arrive from WordPress as absolute; SmartLink strips the CMS origin
// off them, which turns the homepage into either "/" or "" depending on whether
// the stored URL had a trailing slash. Both have to land on the same key.
function normalise(path) {
  if (typeof path !== 'string') return null;
  const clean = path.split('?')[0].split('#')[0];
  if (!clean) return '/';
  if (clean.length > 1 && clean.endsWith('/')) return clean.slice(0, -1);
  return clean;
}

// Speculative fetches are a bandwidth bet on the user's behalf. Don't take it
// on a metered or slow connection — the homepage still works, it just starts
// the way it does today.
function connectionAllowsPrefetch() {
  if (typeof navigator === 'undefined') return false;
  const c = navigator.connection;
  if (!c) return true; // Safari/Firefox: no signal, assume it's fine
  if (c.saveData) return false;
  return !['slow-2g', '2g', '3g'].includes(c.effectiveType);
}

function isFresh(url, ttl) {
  const entry = warmed.get(url);
  if (!entry) return false;
  return Date.now() - entry.at < (entry.ok ? ttl : FAILURE_COOLDOWN_MS);
}

/**
 * Re-open a socket to the video CDN. _document.js's preconnect is spent by the
 * time a client-side navigation happens, and the hint is only honoured while the
 * element is in the document, so the element is moved (removed and re-appended)
 * rather than duplicated on every hover.
 */
export function warmMediaOrigin(origin = MEDIA_ORIGIN) {
  if (typeof document === 'undefined') return;

  if (preconnectEl?.href?.startsWith(origin)) {
    preconnectEl.remove();
  } else {
    preconnectEl = document.createElement('link');
    preconnectEl.rel = 'preconnect';
    preconnectEl.href = origin;
    preconnectEl.crossOrigin = '';
  }

  document.head.appendChild(preconnectEl);
}

// A plain GET with no credentials, which is what hls.js' XhrLoader issues for
// these files (none of them declare EXT-X-BYTERANGE, so there's no Range header
// to match). Same URL + same credentials mode = same HTTP cache entry, so the
// player reads these straight out of cache instead of hitting the network.
async function warmUrl(url, ttl) {
  if (!url || isFresh(url, ttl)) return null;
  warmed.set(url, { at: Date.now(), ok: true });

  try {
    const res = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      // Chrome's Priority Hints on fetch only accept high/low/auto. This is
      // speculative, so it must never contend with the current page.
      priority: 'low',
    });
    if (!res.ok) {
      warmed.set(url, { at: Date.now(), ok: false });
      return null;
    }
    return res;
  } catch {
    warmed.set(url, { at: Date.now(), ok: false });
    return null;
  }
}

function resolveAgainst(base, relative) {
  try {
    return new URL(relative, base).href;
  } catch {
    return null;
  }
}

// First `#EXT-X-STREAM-INF` variant in a master playlist, or the first `.ts`
// in a level playlist — in both cases the first non-comment line. Parsed rather
// than assuming Bunny's `480p/video.m3u8` / `video0.ts` naming, so a video
// without that exact rendition ladder degrades to "no prefetch" instead of two
// 404s.
function firstEntry(text) {
  if (typeof text !== 'string') return null;
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (line && !line.startsWith('#')) return line;
  }
  return null;
}

/**
 * Walk an HLS manifest and warm the three requests standing between a mount and
 * the first painted frame. `startLevel: 0` in the Cinemagraph's config means the
 * player opens on the first variant in the master playlist, which is the one
 * followed here.
 */
export async function prefetchHls(manifestUrl) {
  if (!manifestUrl || !connectionAllowsPrefetch()) return;

  warmMediaOrigin(new URL(manifestUrl).origin);

  const master = await warmUrl(manifestUrl, PLAYLIST_TTL_MS);
  if (!master) return;

  const levelUrl = resolveAgainst(manifestUrl, firstEntry(await master.text()));
  const level = await warmUrl(levelUrl, PLAYLIST_TTL_MS);
  if (!level) return;

  const segmentUrl = resolveAgainst(levelUrl, firstEntry(await level.text()));
  // The segment is ~720KB but cached for 30 days, so it's warmed once per
  // visitor and never re-requested — hence Infinity rather than the playlist
  // TTL, which would re-download it on every hover.
  await warmUrl(segmentUrl, Infinity);
}

/**
 * Fetch the hls.js chunk while the main thread is idle, so a client-side
 * navigation to a page with video doesn't pay a round-trip for it. Unlike the
 * playlists this is safe to do speculatively at any time — the chunk is
 * content-hashed and immutably cached, so it can never go stale.
 *
 * Deliberately behind `requestIdleCallback` *and* the load event: it must never
 * compete with the current page's own images or fonts.
 */
export function warmVideoRuntimeWhenIdle() {
  if (typeof window === 'undefined') return () => {};
  if (!connectionAllowsPrefetch()) return () => {};

  let idleId = null;
  const schedule = () => {
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(() => preloadHls(), {
        timeout: 4000,
      });
    } else {
      idleId = window.setTimeout(() => preloadHls(), 2000);
    }
  };

  if (document.readyState === 'complete') {
    schedule();
    return () => cancelIdle(idleId);
  }

  window.addEventListener('load', schedule, { once: true });
  return () => {
    window.removeEventListener('load', schedule);
    cancelIdle(idleId);
  };
}

function cancelIdle(id) {
  if (id == null) return;
  if (typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(id);
  } else {
    window.clearTimeout(id);
  }
}

/**
 * Declare that navigating to `path` will start playing `manifestUrl`.
 * Called once from _app.js with the homepage hero's video, which travels in
 * globalData so every page knows it.
 */
export function registerRouteMedia(path, manifestUrl) {
  const key = normalise(path);
  if (!key || !manifestUrl) return;
  routeMedia.set(key, manifestUrl);
}

/**
 * Navigation to `path` looks imminent — warm whatever it will play.
 * Safe to call on every hover: repeat calls inside the playlists' 30s cache
 * window are no-ops, and the segment is only ever fetched once.
 */
export function prefetchRouteMedia(path) {
  const key = normalise(path);
  if (!key) return;
  const manifestUrl = routeMedia.get(key);
  if (!manifestUrl) return;
  prefetchHls(manifestUrl);
}
